import { ONE_MINUTE_MS } from '../common/constants/time';
import { getMinutesDiffFromDates } from '../common/utils/time';
import { DEFAULT_DUTY_TIMEZONE } from './ContactWithDutyTracking';
import { computeRequiredRestRaw, flightLegCommercialOps, getDutyFdp, getDutyItemFromLegs, getDutyMaxFdp, } from './DutyCalculator';
import { createNewDutyItem, DUTY_ITEM_TYPE_BREAK, DUTY_ITEM_TYPE_FLIGHT } from './types';
const MAX_ABSOLUTE_FDP_MINUTES = 15 * 60;
const MAX_ABSOLUTE_EXTENDED_FDP_MINUTES = 16 * 60;
export const autocreateDuties = (newFlightLegs, contact, timezone = DEFAULT_DUTY_TIMEZONE) => {
    var _a;
    const userHomebases = contact.baseAirportIcaos;
    const legsFromOldest = [...newFlightLegs];
    legsFromOldest.sort((a, b) => a.timeOut.getTime() - b.timeOut.getTime());
    const newDutyItems = [];
    let lastRequiredRestMinutes = 0;
    let currentNewDutyItem = null;
    let currentDutyLegs = [];
    let currentNewDutyItemSet = null;
    const finalizeNewDutyItem = () => {
        if (currentNewDutyItemSet !== null) {
            markPlannedFdpExtensions(currentNewDutyItemSet, currentDutyLegs);
            newDutyItems.push(...currentNewDutyItemSet);
            currentNewDutyItem = null;
            currentNewDutyItemSet = null;
            currentDutyLegs = [];
        }
    };
    for (const leg of legsFromOldest) {
        const maybeNewDuty = getDutyItemFromLegs(createNewDutyItem(contact, [contact.userId]), [leg], userHomebases);
        const newDutyWouldHaveEnoughRest = currentNewDutyItem &&
            getMinutesDiffFromDates(currentNewDutyItem.endDate, maybeNewDuty.startDate) >= lastRequiredRestMinutes;
        const currentDutyItemIsNotFlight = ((_a = currentDutyLegs[0]) === null || _a === void 0 ? void 0 : _a.reservationEventType) !== undefined;
        const newDutyItemIsNotFlight = leg.reservationEventType !== undefined;
        if (newDutyWouldHaveEnoughRest || currentDutyItemIsNotFlight || newDutyItemIsNotFlight) {
            finalizeNewDutyItem();
        }
        currentNewDutyItem || (currentNewDutyItem = createNewDutyItem(contact, [contact.userId]));
        currentDutyLegs.push(leg);
        currentNewDutyItem = getDutyItemFromLegs(currentNewDutyItem, currentDutyLegs, userHomebases);
        currentNewDutyItemSet = getDutyItemSet(currentNewDutyItem, currentDutyLegs, userHomebases, timezone);
        lastRequiredRestMinutes = computeRequiredRestRaw(currentNewDutyItemSet, userHomebases);
    }
    if (currentNewDutyItem && currentDutyLegs.length) {
        finalizeNewDutyItem();
    }
    return newDutyItems;
};
const markPlannedFdpExtensions = (dutyItems, dutyLegs) => {
    const extendedLegIds = new Set(dutyLegs.filter((leg) => leg.plannedFdpExtension).map((leg) => leg.id));
    if (!extendedLegIds.size) {
        return;
    }
    for (const item of dutyItems) {
        if ([...item.flightLegIds, ...item.logbookRecordIds].some((id) => extendedLegIds.has(id))) {
            item.plannedFdpExtension = true;
        }
    }
};
const getDutyItemSet = (currentNewDutyItem, currentDutyLegsOriginal, userHomebases, timezone) => {
    var _a;
    const currentDutyLegs = [...currentDutyLegsOriginal];
    const outputAccumulator = [];
    const lastLeg = currentDutyLegs.at(-1);
    if (currentNewDutyItem.type === DUTY_ITEM_TYPE_FLIGHT &&
        !flightLegCommercialOps.includes(lastLeg === null || lastLeg === void 0 ? void 0 : lastLeg.ops)) {
        const nonCommercialLegs = [];
        for (let i = currentDutyLegs.length - 1; i >= 0; i--) {
            const leg = currentDutyLegs[i];
            if (flightLegCommercialOps.includes(leg.ops)) {
                break;
            }
            else {
                nonCommercialLegs.push(leg);
            }
        }
        currentDutyLegs.length -= nonCommercialLegs.length;
        currentNewDutyItem = getDutyItemFromLegs(currentNewDutyItem, currentDutyLegs, userHomebases);
        const nonCommercialItem = getDutyItemFromLegs(currentNewDutyItem, nonCommercialLegs, userHomebases);
        nonCommercialItem.startDate = new Date(currentNewDutyItem.endDate);
        nonCommercialItem.startPlaceIcao = lastLeg === null || lastLeg === void 0 ? void 0 : lastLeg.airportOffIcao;
        nonCommercialItem.endPlaceIcao = (_a = lastLeg === null || lastLeg === void 0 ? void 0 : lastLeg.airportOnIcao) !== null && _a !== void 0 ? _a : '?';
        outputAccumulator.push(nonCommercialItem);
    }
    const hasPlannedFdpExtension = currentDutyLegsOriginal.some((leg) => leg.plannedFdpExtension);
    const dutyFdp = getDutyFdp([currentNewDutyItem]);
    const maxFdp = getDutyMaxFdp(false, hasPlannedFdpExtension, currentDutyLegs.length, currentNewDutyItem.startDate, currentNewDutyItem.endDate, timezone);
    if (dutyFdp > maxFdp.limit) {
        const maybeSplitItems = !hasPlannedFdpExtension && attemptSplitDuty(currentNewDutyItem, currentDutyLegs, userHomebases);
        if (maybeSplitItems === false) {
            const maxAbsoluteFdp = hasPlannedFdpExtension
                ? MAX_ABSOLUTE_EXTENDED_FDP_MINUTES
                : MAX_ABSOLUTE_FDP_MINUTES;
            if (dutyFdp > maxAbsoluteFdp) {
                outputAccumulator.push(...forceSeparateDuty(currentNewDutyItem, currentDutyLegs, userHomebases));
            }
            else {
                outputAccumulator.push(currentNewDutyItem);
            }
        }
        else {
            outputAccumulator.push(...maybeSplitItems);
        }
    }
    else {
        outputAccumulator.push(currentNewDutyItem);
    }
    return outputAccumulator;
};
const MAX_SPLIT_END_BASIS_AFTER_TIME_OUT_MS = 30 * 60 * 1e3;
const getSplitEndBasis = (leg) => {
    var _a;
    const latestBasisMs = leg.timeOut.getTime() + MAX_SPLIT_END_BASIS_AFTER_TIME_OUT_MS;
    const basis = (_a = leg.plannedTimeOut) !== null && _a !== void 0 ? _a : leg.timeOut;
    return new Date(Math.min(basis.getTime(), latestBasisMs));
};
const findBiggestGap = (dutyLegs) => {
    let splitBeforeIndex = -1;
    let biggestBreakMinutes = -1;
    for (let i = 1; i < dutyLegs.length; ++i) {
        const previousLeg = dutyLegs[i - 1];
        const thisLeg = dutyLegs[i];
        const breakLengthMinutes = getMinutesDiffFromDates(previousLeg.timeIn, getSplitEndBasis(thisLeg));
        if (breakLengthMinutes > biggestBreakMinutes) {
            biggestBreakMinutes = breakLengthMinutes;
            splitBeforeIndex = i;
        }
    }
    return { splitBeforeIndex, biggestBreakMinutes };
};
const forceSeparateDuty = (originalDutyItem, dutyLegs, userHomebases) => {
    const { userId, crewId } = originalDutyItem;
    const { splitBeforeIndex } = findBiggestGap(dutyLegs);
    if (splitBeforeIndex <= 0) {
        return [originalDutyItem];
    }
    const beforeLegs = dutyLegs.slice(0, splitBeforeIndex);
    const afterLegs = dutyLegs.slice(splitBeforeIndex);
    const beforeDutyItem = getDutyItemFromLegs(originalDutyItem, beforeLegs, userHomebases);
    beforeDutyItem.forceRestAfter = true;
    const afterDutyItem = getDutyItemFromLegs(createNewDutyItem({ userId, crewId }, [userId]), afterLegs, userHomebases);
    return [beforeDutyItem, afterDutyItem];
};
const attemptSplitDuty = (originalDutyItem, dutyLegs, userHomebases) => {
    var _a, _b;
    const { userId } = originalDutyItem;
    const { crewId } = originalDutyItem;
    const { splitBeforeIndex, biggestBreakMinutes } = findBiggestGap(dutyLegs);
    const smallestBreakMinutes = 4 * 60;
    if (splitBeforeIndex > 0 && biggestBreakMinutes >= smallestBreakMinutes) {
        const beforeBreakLegs = dutyLegs.slice(0, splitBeforeIndex);
        const afterBreakLegs = dutyLegs.slice(splitBeforeIndex);
        const beforeBreakDutyItem = getDutyItemFromLegs(originalDutyItem, beforeBreakLegs, userHomebases);
        const lastLegBeforeBreak = beforeBreakLegs.at(-1);
        if (!lastLegBeforeBreak) {
            throw new Error('Cannot make a split break if there are no legs before the split');
        }
        beforeBreakDutyItem.endDate = new Date(lastLegBeforeBreak.timeIn.getTime() + 15 * ONE_MINUTE_MS);
        const afterBreakDutyItem = getDutyItemFromLegs(createNewDutyItem({
            userId,
            crewId,
        }, [userId]), afterBreakLegs, userHomebases);
        afterBreakDutyItem.startDate = new Date(getSplitEndBasis(afterBreakLegs[0]).getTime() - 45 * ONE_MINUTE_MS);
        afterBreakDutyItem.startPlaceIcao = lastLegBeforeBreak.airportOnIcao;
        afterBreakDutyItem.endPlaceIcao = (_b = (_a = dutyLegs.at(-1)) === null || _a === void 0 ? void 0 : _a.airportOnIcao) !== null && _b !== void 0 ? _b : '?';
        const breakDutyItem = createNewDutyItem({ userId, crewId }, [userId]);
        breakDutyItem.type = DUTY_ITEM_TYPE_BREAK;
        breakDutyItem.startDate = new Date(beforeBreakDutyItem.endDate);
        breakDutyItem.endDate = new Date(afterBreakDutyItem.startDate);
        const maybeBreakItems = [
            beforeBreakDutyItem,
            breakDutyItem,
            afterBreakDutyItem,
        ];
        const dutyFdp = getDutyFdp(maybeBreakItems);
        if (dutyFdp <= 12 * 60) {
            return maybeBreakItems;
        }
    }
    return false;
};
//# sourceMappingURL=DutyAutoCreator.js.map